import { Link } from "react-router-dom";
import { ArrowRight, Check, Mail, MapPin, Linkedin, ExternalLink } from "lucide-react";
import SEO from "@/components/SEO";

const OverviewPage = () => {
  return (
    <div className="min-h-screen pt-24 bg-[#FAF9F6]">
      <SEO
        title="Agency Overview - Magnus Kor Talent Partners"
        description="Everything you need to know about Magnus Kor Talent Partners: mission, services, engagement models, pricing, and team. Your talent advisory partner for VC-backed SaaS startups."
        keywords="Magnus Kor overview, talent advisory services, startup hiring pricing, retained search SaaS, contingency hiring tech, embedded recruiter, agency overview"
        canonical="https://magnuskor.com/overview"
        noIndex={true}
      />

      {/* Hero */}
      <section className="py-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="max-w-3xl">
            <span className="text-overline">Agency Overview</span>
            <h1 className="heading-hero mt-4 mb-6">
              Magnus Kor Talent Partners
            </h1>
            <p className="text-body-lg mb-4">
              Talent advisory for VC-backed SaaS startups. From your first IC hire to your leadership bench.
            </p>
            <p className="text-body text-sm">
              Powered by L&D Nexus | London, UK
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <span className="text-overline">Our Mission</span>
              <h2 className="font-display text-2xl mt-3 mb-4">Why we exist</h2>
              <p className="text-body">
                To be the most trusted recruitment partner for VC-backed SaaS startups, delivering
                hires that stay, grow, and drive real business outcomes. We believe startup recruitment
                should be consultative, honest, and focused on long-term fit — not transactional volume.
              </p>
            </div>
            <div>
              <span className="text-overline">Our Vision</span>
              <h2 className="font-display text-2xl mt-3 mb-4">What we're building toward</h2>
              <p className="text-body">
                A world where every startup hire is a considered, strategic decision — where recruiters
                are partners in building companies, not vendors filling seats. We'd rather say no than do it wrong.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <span className="text-[#B8860B] text-xs tracking-[0.25em] uppercase">What We Do</span>
          <h2 className="text-white font-display text-2xl mt-3 mb-10">Core functions we recruit for</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "Engineering", roles: "Software Engineers, Tech Leads, Engineering Managers, VPs & CTOs" },
              { title: "Product & Growth", roles: "Product Managers, Growth Marketers, Demand Gen, Product Leaders" },
              { title: "Go-to-Market", roles: "SDRs, Account Executives, Sales Managers, Revenue Leaders" },
              { title: "Customer Success", roles: "CS Reps, CS Managers, Implementation Specialists, CS Leadership" },
            ].map((fn) => (
              <div key={fn.title} className="p-6 border border-gray-800">
                <h3 className="text-[#B8860B] font-display text-lg mb-3">{fn.title}</h3>
                <p className="text-gray-400 text-sm">{fn.roles}</p>
              </div>
            ))}
          </div>

          <p className="text-gray-500 text-sm mt-8">Seniority range: Individual Contributor (IC) to C-Suite</p>
        </div>
      </section>

      {/* ICP & Exclusions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <span className="text-overline">Who We Serve</span>
              <h2 className="font-display text-2xl mt-3 mb-6">Ideal Client Profile</h2>
              <ul className="space-y-3">
                {[
                  "VC-backed SaaS startups (Seed to Series C+)",
                  "Companies building or scaling core teams",
                  "Founders who value quality over speed",
                  "Teams hiring across Engineering, Product, GTM, or CS",
                  "Geographies: UK, US, Europe, and Remote-first teams",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-gray-700">
                    <Check size={16} className="text-[#B8860B] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <span className="text-overline">Exclusions</span>
              <h2 className="font-display text-2xl mt-3 mb-6">What we don't do</h2>
              <ul className="space-y-3">
                {[
                  "Non-tech / non-SaaS companies",
                  "Staffing, contract, or temporary placements",
                  "Bulk hiring or high-volume recruitment",
                  "Roles outside our core functions (e.g., legal, finance, admin)",
                  "Companies without venture funding or clear growth trajectory",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-gray-500">
                    <span className="text-gray-300 mt-0.5 flex-shrink-0">-</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <span className="text-overline">Our Process</span>
          <h2 className="font-display text-2xl mt-3 mb-10">How we work</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Discovery", desc: "Deep-dive into your product, roadmap, and culture. Not a 15-minute intake call." },
              { step: "02", title: "Sourcing", desc: "Targeted outreach to qualified candidates from our network." },
              { step: "03", title: "Shortlist", desc: "3-5 vetted candidates screened for skill, stage-fit, and motivation." },
              { step: "04", title: "Support", desc: "Interview coordination, offer negotiation, onboarding check-ins through 90 days." },
            ].map((s) => (
              <div key={s.step} className="bg-white p-6">
                <span className="text-[#B8860B] font-display text-2xl">{s.step}</span>
                <h3 className="font-display text-lg mt-3 mb-2">{s.title}</h3>
                <p className="text-body text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Summary */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <span className="text-overline">Engagement Models</span>
          <h2 className="font-display text-2xl mt-3 mb-10">Pricing</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 border border-gray-200" data-testid="pricing-priority">
              <p className="text-xs text-[#B8860B] font-medium tracking-wider uppercase mb-2">Fastest</p>
              <h3 className="font-display text-xl mb-1">Priority Search</h3>
              <p className="font-display text-3xl mb-2">25%</p>
              <p className="text-sm text-gray-500 mb-4">of first-year CTC</p>
              <p className="text-sm text-gray-600">5% upfront + 20% on placement. ~10 day shortlist. 90-day guarantee.</p>
            </div>
            <div className="p-6 border border-gray-200" data-testid="pricing-partnership">
              <p className="text-xs text-gray-500 font-medium tracking-wider uppercase mb-2">No Risk</p>
              <h3 className="font-display text-xl mb-1">Partnership Search</h3>
              <p className="font-display text-3xl mb-2">20%</p>
              <p className="text-sm text-gray-500 mb-4">of first-year CTC</p>
              <p className="text-sm text-gray-600">No upfront cost. Pay only on hire. ~2-3 week shortlist. 60-day guarantee.</p>
            </div>
            <div className="p-6 border-2 border-[#B8860B] bg-[#FAF9F6] relative" data-testid="pricing-growth">
              <span className="absolute -top-3 left-4 bg-[#B8860B] text-white text-xs px-2 py-0.5 font-medium">BEST VALUE</span>
              <p className="text-xs text-[#B8860B] font-medium tracking-wider uppercase mb-2">Recommended</p>
              <h3 className="font-display text-xl mb-1">Growth Partner</h3>
              <p className="font-display text-3xl mb-2">$500<span className="text-lg text-gray-500">/mo</span> + 15%</p>
              <p className="text-sm text-gray-500 mb-4">retainer + success fee</p>
              <p className="text-sm text-gray-600">Continuous sourcing. Unlimited roles. Dedicated account manager. 90-day guarantee.</p>
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-6">
            Fee structures are indicative and subject to a signed engagement agreement.{" "}
            <Link to="/for-startups" className="text-[#B8860B] underline">See full pricing details</Link>{" "}
            or <Link to="/tools" className="text-[#B8860B] underline">calculate your cost</Link>.
          </p>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <span className="text-overline">The Team</span>
          <h2 className="font-display text-2xl mt-3 mb-10">Who you'll work with</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8">
              <h3 className="font-display text-xl mb-1">Manas Kumar</h3>
              <p className="text-[#B8860B] text-sm mb-4">Founder & Strategic Talent Partner</p>
              <p className="text-body text-sm mb-4">
                Leads strategy and client partnerships. Deep experience in startup ecosystems and
                executive recruitment. Focus: understanding the business first, then finding the people who'll help it grow.
              </p>
              <a
                href="https://www.linkedin.com/in/drmanaskumar/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#B8860B] hover:underline"
              >
                <Linkedin size={14} /> LinkedIn
              </a>
            </div>
            <div className="bg-white p-8">
              <h3 className="font-display text-xl mb-1">Aleksandra Kowalewicz</h3>
              <p className="text-[#B8860B] text-sm mb-4">Head of Talent Operations & Delivery</p>
              <p className="text-body text-sm mb-4">
                Drives day-to-day delivery of every search. Manages candidate pipelines, screening processes,
                and client communications. Ensures every search runs with precision and care.
              </p>
              <a
                href="https://www.linkedin.com/in/aleksandra-kowalewicz-assoc-cipd-202025115/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#B8860B] hover:underline"
              >
                <Linkedin size={14} /> LinkedIn
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <span className="text-[#B8860B] text-xs tracking-[0.25em] uppercase">Our Commitments</span>
          <h2 className="text-white font-display text-2xl mt-3 mb-8">What we guarantee</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              "We'll never send unqualified candidates to hit a quota",
              "We'll be transparent about our process and timeline",
              "We'll tell you honestly if something won't work",
              "We'll prioritise fit over speed - every time",
              "If a hire doesn't work out in 90 days, we re-run the search - on us",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 p-4">
                <Check size={16} className="text-[#B8860B] mt-0.5 flex-shrink-0" />
                <p className="text-gray-300 text-sm">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-8 md:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <span className="text-overline">Contact</span>
              <h2 className="font-display text-2xl mt-3 mb-6">Get in touch</h2>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-[#B8860B]" />
                  <a href="mailto:recruitment@magnuskor.com" className="text-sm text-gray-700 hover:text-[#B8860B]">recruitment@magnuskor.com</a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="text-[#B8860B] mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <p className="font-medium">London, UK (HQ)</p>
                    <p className="text-gray-500">71-75 Shelton Street, Covent Garden, WC2H 9JQ</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <ExternalLink size={16} className="text-[#B8860B]" />
                  <a href="https://magnuskor.com" className="text-sm text-gray-700 hover:text-[#B8860B]">magnuskor.com</a>
                </div>
              </div>

              <div className="mt-6 text-xs text-gray-400">
                <p>L&D Nexus Limited (Company No. 16450617) - UK</p>
              </div>
            </div>

            <div className="bg-[#1A1A1A] p-8 md:p-10 text-white flex flex-col justify-center">
              <h3 className="font-display text-2xl mb-4">Ready to start a conversation?</h3>
              <p className="text-gray-400 text-sm mb-6">
                No commitment required. Let's talk about your hiring needs and see if we're the right fit.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/for-startups#contact-form" className="btn-luxury btn-luxury-gold">
                  For Startups <ArrowRight size={14} />
                </Link>
                <Link to="/contact" className="btn-luxury btn-luxury-outline">
                  General Inquiry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OverviewPage;
